import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { formatApiError } from "@/auth/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success(t("auth.resetSuccess"));
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm bg-white";

  return (
    <div className="min-h-screen bg-stone-50" data-testid="reset-password-page">
      <Header />
      <main className="pt-40 sm:pt-60 pb-24 px-6">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-[#e8ece6] flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-6 h-6 text-[#2c4c3b]" />
            </div>
            <h1 className="font-serif text-3xl text-stone-900">{t("auth.resetTitle")}</h1>
          </div>
          <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-stone-100">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-stone-600 mb-2">{t("auth.newPassword")}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className={inputClass}
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="block text-sm text-stone-600 mb-2">{t("auth.confirmPassword")}</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  className={inputClass}
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full py-6"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("auth.resetBtn")}
              </Button>
            </form>
            <Link to="/login" className="block text-center mt-5 text-sm text-[#2c4c3b] hover:underline">
              {t("auth.backToLogin")}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
